
import { useRef, useEffect } from 'react';

/**
 * Custom hook for auto-focus functionality with debounce
 * @param {number} delay - Delay in milliseconds before auto-focusing (default: 1000)
 * @returns {Object} - Object containing focusNext and handleKeyPress functions
 */
const useAutoFocus = (delay = 1000) => {
    const debounceTimerRef = useRef(null);

    /**
     * Focus the next field after user stops typing
     * @param {Object} nextRef - React ref of the next field to focus
     * @param {number} customDelay - Optional custom delay override
     */
    const focusNext = (nextRef, customDelay) => {
        const focusDelay = customDelay !== undefined ? customDelay : delay;

        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new timer to focus next field after delay
        debounceTimerRef.current = setTimeout(() => {
            if (nextRef && nextRef.current) {
                // For Autocomplete fields, focus the input element
                if (nextRef.current.querySelector) {
                    const input = nextRef.current.querySelector('input');
                    if (input) {
                        input.focus();
                    }
                } else {
                    nextRef.current.focus();
                }
            }
        }, focusDelay);
    };

    /**
     * Handle Enter key press for field navigation
     * @param {Object} e - Event object
     * @param {Object} nextRef - React ref of the next field to focus
     */
    const handleKeyPress = (e, nextRef) => {
        if (e.key === 'Enter') {
            e.preventDefault();

            // Clear debounce timer if Enter is pressed
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            if (nextRef && nextRef.current) {
                // For Autocomplete fields, focus the input element
                if (nextRef.current.querySelector) {
                    const input = nextRef.current.querySelector('input');
                    if (input) {
                        input.focus();
                    }
                } else {
                    nextRef.current.focus();
                }
            }
        }
    };

    /**
     * Clear the debounce timer
     */
    const clearTimer = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    };

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    return {
        focusNext,
        handleKeyPress,
        clearTimer
    };
};

export default useAutoFocus;